import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Layout from '../layout/Layout'
import VehicleDetailsCard from '../molecules/VehicleDetailsCard'
import VehicleDocumentsPanel from '../molecules/VehicleDocumentsPanel'
import VehiclePromotionPanel from '../molecules/VehiclePromotionPanel'
import VehiclePhotosPanel from '../molecules/VehiclePhotosPanel'
import { FETCH_BRANDS } from 'src/redux/brandsReducer'
import { FETCH_MOBILE_REFERENCE_VALUES } from 'src/redux/mobileReferenceReducer'
import {
  FETCH_DOCUMENTS,
  FETCH_PDF_URL,
  RESET_PDF_URL,
  SET_CURRENT_PAGE,
  DELETE_DOCUMENT,
  Document
} from 'src/redux/documentsReducer'
import { FETCH_MOTORBIKE, RESET_MOTORBIKE } from 'src/redux/motorbikesReducer'
import {
  DELETE_MOBILE_LISTING,
  FETCH_PROMOTIONS,
  PAUSE_MOBILE_LISTING,
  RESUME_MOBILE_LISTING
} from 'src/redux/promotionsReducer'
import { CREATE_MOTORBIKE_PHOTO, DELETE_MOTORBIKE_PHOTO, FETCH_MOTORBIKE_PHOTOS } from 'src/redux/photosReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { formatPrice } from 'src/helpers/formatPrice'
import { motorbikeEquipmentSpecs } from 'src/helpers/motorbikeEquipmentSpecs'
import { VehicleDocumentType, documentEditPath, vehicleDocumentCreatePath } from 'src/helpers/addVehicleDocument'

const formatRegistrationYear = (firstRegistration: string | null | undefined): string => {
  if (firstRegistration === null || typeof firstRegistration === 'undefined' || firstRegistration.length === 0) {
    return '-'
  }
  const year = firstRegistration.slice(0, 4)
  return /^\d{4}$/.test(year) ? year : '-'
}

const formatMileage = (mileage: number | null | undefined): string => {
  if (mileage === null || typeof mileage === 'undefined') {
    return '-'
  }
  return `${formatPrice(mileage)} km`
}

const formatAmount = (value: number | null | undefined, suffix: string): string => {
  if (value === null || typeof value === 'undefined') {
    return '-'
  }
  return `${formatPrice(value)} ${suffix}`
}

const formatCount = (value: number | null | undefined): string => {
  if (value === null || typeof value === 'undefined') {
    return '-'
  }
  return String(value)
}

const formatVin = (vin: string | null | undefined): string => {
  if (vin === null || typeof vin === 'undefined' || vin.length === 0) {
    return '[VIN]'
  }
  return vin
}

const MotorbikeDetails = (): JSX.Element => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { brands } = useSelector((state: RootReducer) => state.brandsReducer)
  const driveTypes = useSelector((state: RootReducer) => state.mobileReferenceReducer.driveTypes)
  const gearBoxes = useSelector((state: RootReducer) => state.mobileReferenceReducer.gearBoxes)
  const motorbike = useSelector((state: RootReducer) => {
    const { motorbike: current, motorbikeRequestId } = state.motorbikesReducer
    if (current === null || motorbikeRequestId !== id) {
      return null
    }
    return current
  })
  const promotions = useSelector((state: RootReducer) => state.promotionsReducer.promotions)
  const documents = useSelector((state: RootReducer) => state.documentsReducer.documents)
  const currentPage = useSelector((state: RootReducer) => state.documentsReducer.currentPage)
  const totalPages = useSelector((state: RootReducer) => state.documentsReducer.totalPages)
  const pdfUrl = useSelector((state: RootReducer) => state.documentsReducer.pdf_url)
  const pdfIntent = useSelector((state: RootReducer) => state.documentsReducer.pdf_intent)
  const photosState = useSelector((state: RootReducer) => state.photosReducer)
  const photos = photosState.motorbikeId === id ? photosState.photos : []

  useEffect(() => {
    dispatch(FETCH_BRANDS())
    dispatch(FETCH_MOBILE_REFERENCE_VALUES())
    dispatch(SET_CURRENT_PAGE(1))
  }, [dispatch])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_MOTORBIKE(id))
    }
    return () => {
      dispatch(RESET_MOTORBIKE())
    }
  }, [dispatch, id])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_PROMOTIONS(id))
      dispatch(FETCH_MOTORBIKE_PHOTOS(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_DOCUMENTS({ motorbikeId: id }))
    }
  }, [dispatch, id, currentPage])

  useEffect(() => {
    return () => {
      dispatch(RESET_PDF_URL())
    }
  }, [dispatch])

  useEffect(() => {
    if (pdfUrl === '') {
      return
    }
    if (pdfIntent === 'download') {
      const link = window.document.createElement('a')
      link.href = pdfUrl
      link.download = 'document.pdf'
      link.rel = 'noopener noreferrer'
      link.target = '_blank'
      link.click()
    } else {
      window.open(pdfUrl, '_blank')
    }
    dispatch(RESET_PDF_URL())
  }, [dispatch, pdfIntent, pdfUrl])

  const brand = brands.find((item) => item.id === motorbike?.brand_id)
  const brandName = brand?.name ?? motorbike?.brand_name ?? ''
  const title = motorbike === null ? t('my_vehicles.details') : `${brandName} ${motorbike.model}`.trim()

  const handleSelectDocumentType = (documentType: VehicleDocumentType): void => {
    if (typeof id === 'string') {
      navigate(vehicleDocumentCreatePath({ documentType, vehicleId: id }))
    }
  }

  const handleOpenDocument = (document: Document): void => {
    dispatch(FETCH_PDF_URL({ id: document.id, document_type: document.document_type, intent: 'open' }))
  }

  const handleDownloadDocument = (document: Document): void => {
    dispatch(FETCH_PDF_URL({ id: document.id, document_type: document.document_type, intent: 'download' }))
  }

  const handleEditDocument = (document: Document): void => {
    const path = documentEditPath({
      documentType: document.document_type,
      documentId: document.id,
      clientId: document.client_id,
      vehicleId: id
    })
    if (path !== null) {
      navigate(path)
    }
  }

  const handleDeleteDocument = (document: Document): void => {
    dispatch(
      DELETE_DOCUMENT({
        id: document.id,
        document_type: document.document_type,
        ...(typeof id === 'string' ? { motorbikeId: id } : {})
      })
    )
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <VehicleDetailsCard
          title={title}
          status={motorbike?.status ?? null}
          specs={[
            { label: t('my_vehicles.brand'), value: brandName === '' ? '-' : brandName },
            { label: t('my_vehicles.model'), value: motorbike?.model ?? '-' },
            { label: t('my_vehicles.mileage'), value: formatMileage(motorbike?.total_mileage) },
            { label: t('my_vehicles.year'), value: formatRegistrationYear(motorbike?.first_registration) },
            {
              label: t('my_vehicles.promotion_capacity'),
              value: formatAmount(motorbike?.capacity, t('my_vehicles.ccm'))
            },
            {
              label: t('my_vehicles.promotion_power'),
              value: formatAmount(motorbike?.engine_power, t('my_vehicles.kw'))
            },
            {
              label: t('my_vehicles.promotion_owners'),
              value: formatCount(motorbike?.number_of_previous_owners)
            },
            { label: t('my_vehicles.vin'), value: formatVin(motorbike?.vin), wide: true }
          ]}
          detailSpecs={motorbikeEquipmentSpecs({ motorbike, driveTypes, gearBoxes, t })}
          onBack={() => {
            navigate('/selling/vehicles')
          }}
          onEdit={() => {
            if (typeof id === 'string') {
              navigate(`/selling/vehicles/${id}/edit`)
            }
          }}
        />
        <VehiclePhotosPanel
          photos={photos}
          onAdd={(file) => {
            if (typeof id === 'string') {
              dispatch(CREATE_MOTORBIKE_PHOTO({ motorbikeId: id, file }))
            }
          }}
          onDelete={(photoId) => {
            if (typeof id === 'string') {
              dispatch(DELETE_MOTORBIKE_PHOTO({ motorbikeId: id, photoId }))
            }
          }}
        />
        <div className="self-stretch flex-1 relative inline-flex justify-start items-start gap-4">
          <VehiclePromotionPanel
            promotions={promotions}
            onAddPromotion={(portal) => {
              if (typeof id === 'string') {
                navigate(`/selling/vehicles/${id}/promotions/new?portal=${encodeURIComponent(portal)}`)
              }
            }}
            onEditPromotion={(promotionId) => {
              if (typeof id === 'string') {
                navigate(`/selling/vehicles/${id}/promotions/${promotionId}/edit`)
              }
            }}
            onPausePromotion={(promotionId) => {
              if (typeof id === 'string') {
                dispatch(PAUSE_MOBILE_LISTING({ motorbikeId: id, promotionId }))
              }
            }}
            onResumePromotion={(promotionId) => {
              if (typeof id === 'string') {
                dispatch(RESUME_MOBILE_LISTING({ motorbikeId: id, promotionId }))
              }
            }}
            onDeletePromotion={(promotionId) => {
              if (typeof id === 'string') {
                dispatch(DELETE_MOBILE_LISTING({ motorbikeId: id, promotionId }))
              }
            }}
          />
          <VehicleDocumentsPanel
            documents={documents}
            currentPage={currentPage}
            totalPages={totalPages}
            onSelectDocumentType={handleSelectDocumentType}
            onOpenDocument={handleOpenDocument}
            onDownloadDocument={handleDownloadDocument}
            onEditDocument={handleEditDocument}
            onDeleteDocument={handleDeleteDocument}
            onPageChange={(page) => {
              dispatch(SET_CURRENT_PAGE(page))
            }}
          />
        </div>
      </div>
    </Layout>
  )
}

export default MotorbikeDetails
